import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabaseClient';
import { usePresentationRemoteController } from '../../hooks/usePresentationRemoteController';
import type { RemoteConnectionStatus, RemotePresenceState } from '../../types/presentationRemote';
import { REMOTE_NAME_MAX } from '../../utils/presentationRemoteIdentity';
import RemoteControllerChat from './RemoteControllerChat';
import { usePaidPassGate } from '../../contexts/PaidPassGateContext';
import { REMOTE_DESIGN_CLASS } from '../../utils/remoteDesignTheme';
import './RemoteControllerPanel.css';
import './RemoteDesignThemes.css';
import './RemoteReconnectButton.css';

const STATUS_LABEL: Record<RemoteConnectionStatus, string> = {
  idle: '대기',
  local: '이 기기 연결',
  limited: '원격 혼잡',
  connecting: '연결 중',
  connected: '연결됨',
  reconnecting: '재연결 중',
  error: '연결 오류',
  disconnected: '연결 끊김',
  closed: '연결 종료',
};

function appRootHref(): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/`;
}


const RenameModal: React.FC<{
  target: RemotePresenceState;
  onClose: () => void;
  onSubmit: (name: string) => Promise<boolean>;
}> = ({ target, onClose, onSubmit }) => {
  const [value, setValue] = useState(target.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const ok = await onSubmit(value);
    setBusy(false);
    if (ok) onClose();
    else setError('이름을 바꾸지 못했어요. 프레젠테이션 탭이 열려 있는지 확인해 주세요.');
  };
  return createPortal(
    <div className="cp-modal-backdrop" onMouseDown={onClose} style={{ zIndex: 10001 }}>
      <form
        className={`cp-modal cp-remote-rename-modal ${REMOTE_DESIGN_CLASS}`}
        role="dialog"
        aria-modal="true"
        aria-label="프레젠테이션 이름 변경"
        onMouseDown={(ev) => ev.stopPropagation()}
        onSubmit={submit}
      >
        <div className="cp-modal-body">
          <h2 className="cp-remote-section-title">이름 변경</h2>
          <input
            className="cp-remote-input"
            value={value}
            maxLength={REMOTE_NAME_MAX}
            autoFocus
            onChange={(e) => setValue(e.target.value)}
            placeholder="비우면 자동 이름으로 돌아갑니다"
          />
          {error ? <p className="cp-remote-rename-error" role="alert">{error}</p> : null}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-remote-btn" onClick={onClose} disabled={busy}>
            취소
          </button>
          <button type="submit" className="cp-remote-btn cp-remote-btn--primary" disabled={busy}>
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

const RemoteControllerInner: React.FC<{ userId: string; headerExtra?: React.ReactNode }> = ({ userId, headerExtra }) => {
  const { canUseFeature, openPaidPassRequiredModal } = usePaidPassGate();
  const ctl = usePresentationRemoteController(userId, canUseFeature('remote_control'));
  const { status, rename, reconnect } = ctl;
  const showReconnect = ctl.remoteActive && (status === 'disconnected' || status === 'error' || status === 'limited');
  const [renameTarget, setRenameTarget] = useState<RemotePresenceState | null>(null);

  return (
    <div className={`cp-remote-panel cp-remote-panel--chat ${REMOTE_DESIGN_CLASS}`}>
      <header className="cp-remote-head">
        <h1 className="cp-remote-title">프레젠테이션 리모트</h1>
        <div className="cp-remote-head-right">
          <span className={`cp-remote-status is-${status}`} role="status" aria-live="polite">
            <span className="cp-remote-status-dot" aria-hidden />
            {STATUS_LABEL[status]}
          </span>
          {showReconnect ? (
            <button type="button" className="cp-remote-reconnect-btn" onClick={reconnect}>
              다시 연결
            </button>
          ) : null}
          {headerExtra}
        </div>
      </header>

      <RemoteControllerChat
        ctl={ctl}
        userId={userId}
        onRename={setRenameTarget}
        onOpenPaidGuide={openPaidPassRequiredModal}
      />

      {renameTarget ? (
        <RenameModal
          target={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSubmit={(name) => rename(renameTarget.sessionId, name)}
        />
      ) : null}
    </div>
  );
};

export interface RemoteControllerPanelProps {
  /** 헤더 오른쪽 (예: 모달 닫기 버튼) */
  headerExtra?: React.ReactNode;
}

/** 리모컨 UI — /remote 페이지와 메인 앱 모달 공용. 마운트된 동안만 Realtime 연결 */
const RemoteControllerPanel: React.FC<RemoteControllerPanelProps> = ({ headerExtra }) => {
  const [auth, setAuth] = useState<{ state: 'loading' | 'none' | 'ok'; userId?: string }>({ state: 'loading' });

  useEffect(() => {
    let alive = true;
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!alive) return;
        const id = data.session?.user?.id;
        setAuth(id ? { state: 'ok', userId: id } : { state: 'none' });
      })
      .catch(() => alive && setAuth({ state: 'none' }));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!alive) return;
      const id = session?.user?.id;
      setAuth(id ? { state: 'ok', userId: id } : { state: 'none' });
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (auth.state === 'loading') {
    return (
      <div className="cp-remote-panel" role="status" aria-busy="true">
        <p className="cp-remote-hint">불러오는 중…</p>
      </div>
    );
  }
  if (auth.state === 'none' || !auth.userId) {
    return (
      <div className="cp-remote-panel">
        <div className="cp-remote-gate">
          <div className="cp-remote-head" style={{ width: '100%' }}>
            <h1 className="cp-remote-title">로그인이 필요해요</h1>
            {headerExtra}
          </div>
          <p className="cp-remote-hint">리모트는 메인 프레젠테이션과 같은 계정으로 로그인해야 사용할 수 있습니다.</p>
          <a className="cp-remote-btn cp-remote-btn--primary" href={appRootHref()} style={{ display: 'inline-flex', alignItems: 'center' }}>
            로그인하러 가기
          </a>
        </div>
      </div>
    );
  }
  return <RemoteControllerInner userId={auth.userId} headerExtra={headerExtra} />;
};

export default RemoteControllerPanel;
